import React, { useCallback, useEffect, useId, useRef } from 'react';
import './GlowingOrb.css';
import {
  type OrbState,
  type OrbShape,
  type OrbPattern,
  type OrbForm,
  type Palette,
  type RGB,
  type DotFieldState,
  type RingGL,
  SMOOTHING,
  STATE_TWEEN_MS,
  BIN_COUNT,
  VOLUME_CEILING,
  SPEAK_PEAK,
  FLOAT_AMOUNT_X,
  FLOAT_AMOUNT_Y,
  FLOAT_FREQ_X,
  FLOAT_FREQ_Y,
  RING_WAVE_SPEED,
  RING_BOOST_PEAK,
  RING_STROKE_THIN,
  RING_STROKE_THICK,
  RING_WAVE_ATTACK,
  SHAPE_WAVE_GAIN,
  RING_BLOOM_WIDTH_MUL,
  RING_BLOOM_ALPHA,
  INNER_WAVE_SPEED,
  SHAPE_TWEEN_MS,
  PATTERN_TWEEN_MS,
  MOTION_GAIN_SMALL,
  MOTION_GAIN_LARGE,
  MOTION_SIZE_MIN,
  MOTION_SIZE_MAX,
  DOT_GRID,
  PALETTE_READY,
  PALETTE_SPEAKING,
  PALETTE_CONNECTING,
  easeInOutCubic,
  lerp,
  lerpRGB,
  buildInnerClipPath,
  buildRingPath,
  applyDotMask,
  initRingGL,
} from './glowingOrbInternals';
import { mountLiquidCore, type LiquidCoreHandle } from './glowingOrbLiquidCore';

export type { OrbState, OrbShape, OrbPattern, OrbForm } from './glowingOrbInternals';

/**
 * Props for {@link GlowingOrb}.
 */
interface GlowingOrbProps {
  /** Live conversational state. Drives palette, amplitude target, and (unless
      overridden) the form choreography. */
  state: OrbState;
  /** Required when `state === 'speaking'` for audio-reactive scaling. Safe to
      pass always; null/undefined falls back to a gentle idle pulse. */
  analyserNode?: AnalyserNode | null;
  /** Extra classes merged onto the root wrapper. */
  className?: string;
  /**
   * Explicit form override. When set, the internal state-driven roaming is
   * suspended and she holds this form, e.g. `'eyes'` while the respondent
   * shares their screen. `'orb'` is the whole merged circle; any other value is
   * the dot field animating that emotion. Leave undefined to let her choreograph
   * herself from `state` (listening roams calm to playful, connecting ripples,
   * speaking gathers whole), per docs/DESIGN.md, "Her shapes".
   */
  form?: OrbForm;
  /**
   * When set, the core settles into a pill button holding this label (the one
   * moment she is pressable). Her rim, halo, and liquid stay alive around it.
   */
  cta?: string | null;
}

/* States choreograph her form (docs/DESIGN.md, "Her shapes"): connecting
   ripples through the dot field, speaking gathers her into one circle, and
   while listening she chooses her own shape, calm forms at first then warmer
   and more playful ones the longer she has been listening. */
const ROAM_CALM: OrbForm[] = ['orb', 'breathe', 'wave'];
const ROAM_SETTLED: OrbForm[] = ['breathe', 'wave', 'ripple', 'pulse', 'orb'];
const ROAM_PLAYFUL: OrbForm[] = ['sparkle', 'spin', 'eyes', 'heart', 'breathe', 'orb'];

/* CTA clip geometry. The geometry is a CLIP tween, never a size tween: resizing
   the core re-grids the fluid sim every frame (it glitches), and the clip keeps
   the pill inside the core's circle so it can never poke past the rim. */
const CTA_CLIP = 'inset(32% 0 round 9999px)'; // pill: full core width, 36% tall
const CIRCLE_CLIP = 'inset(1% round 9999px)'; // the inset twin of the circle, so the clip can tween

/* Platform feel tuning for the real-mic speaking path (the brand guide uses a
   synthetic envelope and does not need these). The release is quicker than the
   shared SMOOTHING so the gaps between words actually dip instead of riding a
   slow decay, and the below-rest core contraction is steeper than the brand
   guide's 0.9 so silence reads as a real shrink beneath her resting size:
   more movement is allowed downward. */
const SPEAK_RELEASE = 0.2;       // amplitude fall rate while speaking (vs SMOOTHING)
const CORE_CONTRACT_SLOPE = 1.4; // below-rest core scale slope (steeper than the 0.9 above-rest brand-guide default)
const CORE_MIN_SCALE = 0.9;      // floor on the contraction: a long silence settles here instead of collapsing to a tiny disc that then snaps back up to resting size

/**
 * hollie's living orb. Canvas + WebGL, cursor-reactive, built from layered
 * radial gradients (liquid core, churn rim, halo, breathing aura) with a
 * dot-matrix shape morph for her emotion patterns. The house reference for
 * hollie's look and motion is the brand guide orb (docs/DESIGN.md); this is the
 * product mirror, with the one platform difference that speaking is driven by a
 * real microphone analyser rather than a synthetic envelope.
 */
const GlowingOrb: React.FC<GlowingOrbProps> = ({ state, analyserNode, className = '', form, cta = null }) => {
  // Stable per-instance ids for the inner clip path and ring gradient.
  const uid = useId();
  const clipId = `orb-inner-clip-${uid}`;
  const ringId = `orb-ring-paint-${uid}`;

  // Layer refs: all animation flows through direct DOM writes to avoid re-renders.
  const rootRef = useRef<HTMLDivElement>(null);
  const floatRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const ringSvgRef = useRef<SVGSVGElement>(null);
  const ringRef = useRef<SVGPathElement>(null);
  const ringBloomRef = useRef<SVGPathElement>(null);
  const ringGradRef = useRef<SVGLinearGradientElement>(null);
  const ringStop0Ref = useRef<SVGStopElement>(null);
  const ringStop1Ref = useRef<SVGStopElement>(null);
  const ringStop2Ref = useRef<SVGStopElement>(null);
  const ringStop3Ref = useRef<SVGStopElement>(null);
  const coreRef = useRef<HTMLDivElement>(null);
  const innerClipPathRef = useRef<SVGPathElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hlRef = useRef<HTMLDivElement>(null);
  const ctaLabelRef = useRef<HTMLDivElement>(null);
  const ringGLCanvasRef = useRef<HTMLCanvasElement>(null);

  // Persisted animation state across frames.
  const amplitudeRef = useRef(0);
  /** 0 = fully ready (listening/speaking palette), 1 = fully connecting. */
  const connRef = useRef(state === 'connecting' ? 1 : 0);
  const connTweenRef = useRef<{ from: number; to: number; startMs: number; duration: number } | null>(null);
  const ringPhaseRef = useRef(0);
  const innerPhaseRef = useRef(0);
  const lastFrameTRef = useRef<number | null>(null);
  const blobTimeRef = useRef(0);
  /** Decaying sprint level: gives the whoosh its long tail after a tween. */
  const whooshRef = useRef(0);
  const motionGainRef = useRef(MOTION_GAIN_LARGE);

  // Shape morph + emotion pattern state.
  /** 0 = one merged circle, 1 = fully split dot field. */
  const morphRef = useRef(0);
  const morphTweenRef = useRef<{ from: number; to: number; startMs: number; duration: number } | null>(null);
  const shapeRef = useRef<OrbShape>('orb');
  const patternRef = useRef<OrbPattern>('breathe');
  const patternTweenRef = useRef<{ startMs: number; duration: number } | null>(null);
  const fieldStateRef = useRef<DotFieldState>({ last: new Float64Array(DOT_GRID.length), from: null, blend: 1 });

  // CTA form state.
  const ctaOnRef = useRef(false);
  const ctaMixRef = useRef(0);

  // Listening roam state.
  const listenStartRef = useRef(0);
  const roamTimerRef = useRef<number | null>(null);

  // Cursor ripple state for the WebGL rim.
  const pointerRef = useRef({ x: 0, y: 0, proxTarget: 0, rotTarget: 0, proxS: 0, rotS: 0 });

  // Live engines.
  const etherRef = useRef<LiquidCoreHandle | null>(null);

  // Prop mirrors so the rAF loop reads current values without re-mounting.
  const stateRef = useRef<OrbState>(state);
  const analyserRef = useRef<AnalyserNode | null | undefined>(analyserNode);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { analyserRef.current = analyserNode; }, [analyserNode]);

  /* ---- Form controller (operates on refs; stable identities) ---- */

  const setShape = useCallback((next: OrbShape) => {
    if (next === shapeRef.current) return;
    morphTweenRef.current = { from: morphRef.current, to: next === 'dots' ? 1 : 0, startMs: performance.now(), duration: SHAPE_TWEEN_MS };
    shapeRef.current = next;
    rootRef.current?.setAttribute('data-shape', next);
  }, []);

  const setPattern = useCallback((next: OrbPattern) => {
    if (next === patternRef.current) return;
    // Snapshot the current field (even mid-blend) and cross-fade from there, so
    // rapid pattern hops stay continuous.
    const fs = fieldStateRef.current;
    fs.from = fs.last.slice();
    fs.blend = 0;
    patternTweenRef.current = { startMs: performance.now(), duration: PATTERN_TWEEN_MS };
    patternRef.current = next;
  }, []);

  const applyForm = useCallback((f: OrbForm) => {
    if (f === 'orb') setShape('orb');
    else { setPattern(f); setShape('dots'); }
  }, [setShape, setPattern]);

  const stopRoam = useCallback(() => {
    if (roamTimerRef.current !== null) { clearTimeout(roamTimerRef.current); roamTimerRef.current = null; }
  }, []);

  const scheduleRoam = useCallback((delay: number) => {
    stopRoam();
    roamTimerRef.current = window.setTimeout(() => {
      const heard = (performance.now() - listenStartRef.current) / 1000;
      const pool = heard < 12 ? ROAM_CALM : heard < 30 ? ROAM_SETTLED : ROAM_PLAYFUL;
      const current: OrbForm = shapeRef.current === 'orb' ? 'orb' : patternRef.current;
      const choices = pool.filter((f) => f !== current);
      const next = choices[Math.floor(Math.random() * choices.length)] ?? 'orb';
      applyForm(next);
      scheduleRoam(5000 + Math.random() * 6000);
    }, delay);
  }, [stopRoam, applyForm]);

  // Kick a conn tween whenever `state` flips (palette wash + ring ripple).
  useEffect(() => {
    connTweenRef.current = {
      from: connRef.current,
      to: state === 'connecting' ? 1 : 0,
      startMs: performance.now(),
      duration: STATE_TWEEN_MS,
    };
  }, [state]);

  // Choreograph her form from state, unless an explicit `form` or `cta` owns it.
  useEffect(() => {
    if (cta) { stopRoam(); return undefined; } // the CTA pill owns the form while it is up
    stopRoam();
    if (form !== undefined) {
      applyForm(form);
      return undefined;
    }
    if (state === 'connecting') {
      applyForm('ripple'); // processing/loading ripples through the dots
    } else if (state === 'speaking') {
      applyForm('orb'); // she gathers whole and her voice moves it
    } else {
      // Listening: enter whole, then drift between shapes on her own.
      listenStartRef.current = performance.now();
      applyForm('orb');
      scheduleRoam(4000 + Math.random() * 4000);
    }
    return () => stopRoam();
  }, [state, form, cta, applyForm, scheduleRoam, stopRoam]);

  // CTA pill: ease the core's clip between her circle and a pill button.
  useEffect(() => {
    const core = coreRef.current;
    const root = rootRef.current;
    const label = ctaLabelRef.current;
    const on = !!cta;
    if (label && cta) label.textContent = cta;
    if (on === ctaOnRef.current) return undefined;
    ctaOnRef.current = on;
    let timer: number | null = null;
    if (on) {
      setShape('orb'); // she merges whole before she becomes the action
      root?.setAttribute('data-cta', '');
      // The wavy url() clip cannot interpolate; swap to the circle's inset twin
      // (the wave is imperceptible), flush, then ease to the pill.
      if (core) {
        core.style.clipPath = CIRCLE_CLIP;
        void core.offsetWidth;
        core.style.clipPath = CTA_CLIP;
      }
    } else {
      root?.removeAttribute('data-cta');
      if (core) core.style.clipPath = CIRCLE_CLIP;
      // Once the pill has eased back to the circle, restore the living wavy clip.
      timer = window.setTimeout(() => {
        if (core) core.style.clipPath = `url(#${clipId})`;
      }, 750);
    }
    return () => { if (timer !== null) clearTimeout(timer); };
  }, [cta, setShape, clipId]);

  // Track rendered size so motion gain compensates for small mobile renders.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      const t = Math.max(0, Math.min(1, (size - MOTION_SIZE_MIN) / (MOTION_SIZE_MAX - MOTION_SIZE_MIN)));
      motionGainRef.current = MOTION_GAIN_SMALL + (MOTION_GAIN_LARGE - MOTION_GAIN_SMALL) * t;
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // rAF loop: mounts ONCE and never restarts. Reads live props via refs so
  // neither restarts the loop (keeping `elapsed` continuous across state flips).
  useEffect(() => {
    const start = performance.now();
    let dataArray: Uint8Array<ArrayBuffer> | null = null;
    let lastAnalyser: AnalyserNode | null | undefined = undefined;

    // Mount the liquid core into the core box. It paints as an in-flow canvas,
    // so it sits BENEATH the absolutely-positioned 2D overlay, specular
    // highlight, and CTA label: the glass highlight and voice bloom read on top
    // of the liquid when merged, the dots read on top when she splits.
    if (coreRef.current) {
      etherRef.current = mountLiquidCore(coreRef.current, {
        bgColor: '#8672CC',
        colors: ['#5227FF', '#9D5CFF', '#FF9FFC', '#B19EEF'],
        bulge: 0.18,
        washAmp: 0.04,
        externalLoop: true,
        autoSpeed: 0.1,
        mouseForce: 14,
      });
    }

    // The WebGL rim replaces both the SVG ring AND the legacy blur-ball glow.
    // Running both glow systems at once muddies the orb. If WebGL is
    // unavailable (jsdom, old GPUs), the SVG ring + glow stay as the fallback.
    // Init the WebGL rim once per canvas and cache it ON the canvas element, so
    // React StrictMode's mount -> cleanup -> mount cycle (and any remount that
    // reuses the same canvas) reuses the live context instead of re-initialising
    // on a dead one. We deliberately do NOT lose the context in cleanup: doing
    // so would kill the cached context, and the StrictMode remount would then
    // fail to re-init and leave the rim blank. Real unmounts drop the canvas and
    // the GC reclaims the context.
    const glCanvas = ringGLCanvasRef.current as (HTMLCanvasElement & { _orbRingGL?: RingGL | null }) | null;
    let ringGL: RingGL | null = null;
    if (glCanvas) {
      if (glCanvas._orbRingGL === undefined) glCanvas._orbRingGL = initRingGL(glCanvas);
      ringGL = glCanvas._orbRingGL;
    }
    // Select the rim explicitly every mount: when the WebGL rim is live, hide the
    // SVG ring + legacy glow; otherwise fall back to the visible SVG ring. This
    // never leaves both hidden, even if a re-init fails.
    if (ringSvgRef.current) ringSvgRef.current.style.display = ringGL ? 'none' : '';
    if (glowRef.current) glowRef.current.style.display = ringGL ? 'none' : '';
    if (ringGLCanvasRef.current) ringGLCanvasRef.current.style.display = ringGL ? '' : 'none';

    // Cursor ripple on the shader rim: a soft band of light plus a slight tilt
    // toward the pointer. No warp, the band never bends.
    const onPointerMove = (ev: PointerEvent) => {
      const cv = ringGLCanvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cx = Math.min(Math.max(ev.clientX, rect.left), rect.right);
      const cy = Math.min(Math.max(ev.clientY, rect.top), rect.bottom);
      const px = (cx - rect.left) * dpr;
      const py = (rect.bottom - cy) * dpr;
      const nx = (px / Math.max(1, cv.width)) * 2 - 1;
      const ny = (py / Math.max(1, cv.height)) * 2 - 1;
      const p = pointerRef.current;
      p.x = px; p.y = py; p.proxTarget = 1; p.rotTarget = nx * 0.12 + ny * 0.06;
    };
    const onPointerLeave = () => { pointerRef.current.proxTarget = 0; pointerRef.current.rotTarget = 0; };
    const rootEl = rootRef.current;
    if (ringGL && rootEl) {
      rootEl.addEventListener('pointermove', onPointerMove);
      rootEl.addEventListener('pointerleave', onPointerLeave);
    }

    let rafId = 0;

    const animate = (t: number) => {
      const elapsed = (t - start) / 1000;
      const liveState = stateRef.current;
      const analyser = analyserRef.current;
      const ether = etherRef.current;

      // Re-alloc the byte buffer only when the underlying analyser changes.
      if (analyser !== lastAnalyser) {
        dataArray = analyser ? new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)) : null;
        lastAnalyser = analyser;
      }

      // ---- 1. Amplitude target per state. Speaking uses the real mic. ----
      let target: number;
      if (liveState === 'speaking' && analyser && dataArray) {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        const bins = Math.min(BIN_COUNT, dataArray.length);
        for (let i = 0; i < bins; i++) sum += dataArray[i];
        // Cap at SPEAK_PEAK so a hot mix can never swell the core into the rim;
        // the pulse plays out in the range below the cap.
        target = Math.min(SPEAK_PEAK, sum / bins / VOLUME_CEILING);
      } else if (liveState === 'connecting') {
        target = 0.28 + 0.12 * Math.sin(elapsed * 1.8);
      } else {
        target = 0.08 + 0.04 * Math.sin(elapsed * 0.9);
      }
      // Voice onsets are abrupt, decays are not: rise fast, then release. While
      // speaking the release is quicker (SPEAK_RELEASE) so word gaps visibly dip.
      const smoothing = liveState === 'speaking'
        ? (target > amplitudeRef.current ? 0.3 : SPEAK_RELEASE)
        : SMOOTHING;
      amplitudeRef.current += (target - amplitudeRef.current) * smoothing;
      const amp = amplitudeRef.current;
      const motionAmp = amp * motionGainRef.current;

      // ---- 2. Tweens: conn (state), morph (shape), pattern cross-fade. ----
      let tweenProgress: number | null = null;
      const tween = connTweenRef.current;
      if (tween) {
        tweenProgress = Math.min(1, (t - tween.startMs) / tween.duration);
        connRef.current = tween.from + (tween.to - tween.from) * easeInOutCubic(tweenProgress);
        if (tweenProgress >= 1) connTweenRef.current = null;
      }
      const conn = connRef.current;

      let morphProgress: number | null = null;
      const mTween = morphTweenRef.current;
      if (mTween) {
        morphProgress = Math.min(1, (t - mTween.startMs) / mTween.duration);
        morphRef.current = mTween.from + (mTween.to - mTween.from) * easeInOutCubic(morphProgress);
        if (morphProgress >= 1) morphTweenRef.current = null;
      }
      const morph = morphRef.current;

      const pTween = patternTweenRef.current;
      if (pTween) {
        const p = Math.min(1, (t - pTween.startMs) / pTween.duration);
        fieldStateRef.current.blend = easeInOutCubic(p);
        if (p >= 1) { patternTweenRef.current = null; fieldStateRef.current.from = null; fieldStateRef.current.blend = 1; }
      }

      const dt = lastFrameTRef.current === null ? 0 : Math.max(0, (t - lastFrameTRef.current) / 1000);
      lastFrameTRef.current = t;

      // ---- Per-frame palette: READY -> SPEAKING by amp, then -> CONNECTING by conn. ----
      const sb = Math.min(1, amp * 1.3);
      const rB = lerpRGB(PALETTE_READY.base, PALETTE_SPEAKING.base, sb);
      const r0 = lerpRGB(PALETTE_READY.stop0, PALETTE_SPEAKING.stop0, sb);
      const r1 = lerpRGB(PALETTE_READY.stop1, PALETTE_SPEAKING.stop1, sb);
      const r2 = lerpRGB(PALETTE_READY.stop2, PALETTE_SPEAKING.stop2, sb);
      const rBl = lerpRGB(PALETTE_READY.bloom, PALETTE_SPEAKING.bloom, sb);
      const rR0 = lerpRGB(PALETTE_READY.ring0, PALETTE_SPEAKING.ring0, sb);
      const rR1 = lerpRGB(PALETTE_READY.ring1, PALETTE_SPEAKING.ring1, sb);
      const rR2 = lerpRGB(PALETTE_READY.ring2, PALETTE_SPEAKING.ring2, sb);
      const rR3 = lerpRGB(PALETTE_READY.ring3, PALETTE_SPEAKING.ring3, sb);
      const palette: Palette = {
        base: lerpRGB(rB, PALETTE_CONNECTING.base, conn),
        stop0: lerpRGB(r0, PALETTE_CONNECTING.stop0, conn),
        stop1: lerpRGB(r1, PALETTE_CONNECTING.stop1, conn),
        stop2: lerpRGB(r2, PALETTE_CONNECTING.stop2, conn),
        bloom: lerpRGB(rBl, PALETTE_CONNECTING.bloom, conn),
        ring0: lerpRGB(rR0, PALETTE_CONNECTING.ring0, conn),
        ring1: lerpRGB(rR1, PALETTE_CONNECTING.ring1, conn),
        ring2: lerpRGB(rR2, PALETTE_CONNECTING.ring2, conn),
        ring3: lerpRGB(rR3, PALETTE_CONNECTING.ring3, conn),
      };

      // ---- 3. Float drift (every state): vertical-dominant bob. ----
      if (floatRef.current) {
        const fx = Math.sin(elapsed * FLOAT_FREQ_X) * FLOAT_AMOUNT_X;
        const fy = Math.sin(elapsed * FLOAT_FREQ_Y + 1.3) * FLOAT_AMOUNT_Y;
        floatRef.current.style.transform = `translate(${fx.toFixed(2)}%, ${fy.toFixed(2)}%)`;
      }

      // ---- 4. Core scale + soft shadow. The swell is the voice signal; the rim
      //         stays still. Asymmetric around the listening rest: gentle above
      //         (peak voice stays clear of the rim), steep below so silence reads
      //         as a real contraction. The button holds still (ctaMix eases the
      //         swell out). ----
      if (coreRef.current) {
        ctaMixRef.current += ((ctaOnRef.current ? 1 : 0) - ctaMixRef.current) * 0.08;
        const ctaMix = ctaMixRef.current;
        const d = motionAmp - 0.1 * motionGainRef.current;
        const liveScale = Math.max(CORE_MIN_SCALE, 1 + (d >= 0 ? d * 0.2 : d * CORE_CONTRACT_SLOPE));
        coreRef.current.style.transform = `scale(${lerp(liveScale, 1, ctaMix)})`;
        const shadowSize = 40 + (1 - conn) * (10 + motionAmp * 14);
        const shadowAlpha = 0.30 + (1 - conn) * (0.12 + motionAmp * 0.14);
        const s2 = palette.stop2;
        coreRef.current.style.boxShadow = `0 0 ${shadowSize.toFixed(1)}px rgba(${s2[0] | 0},${s2[1] | 0},${s2[2] | 0},${shadowAlpha.toFixed(3)})`;
      }

      // ---- 4b. Specular highlight: glass on the merged orb only. ----
      if (hlRef.current) hlRef.current.style.opacity = ((1 - morph) * (1 - ctaMixRef.current)).toFixed(3);

      // ---- 5. Legacy glow (SVG fallback only; hidden when the GL rim is live). ----
      if (glowRef.current && !ringGL) {
        glowRef.current.style.transform = `scale(${1 + motionAmp * 0.7})`;
        glowRef.current.style.opacity = `${0.20 + motionAmp * 0.4}`;
        const s2 = palette.stop2;
        glowRef.current.style.backgroundColor = `rgba(${s2[0] | 0},${s2[1] | 0},${s2[2] | 0},0.28)`;
      }

      // ---- 6+7. The rim. Its life: noise drift, colour churn, orbiting light,
      //           and the connecting wave (one bump travelling the band,
      //           amplitude = conn), sprinting on state/shape tweens. ----
      const statePulse = tweenProgress !== null ? Math.sin(tweenProgress * Math.PI) : 0;
      // Shape morphs play one pass of the wave scaled by SHAPE_WAVE_GAIN: a
      // subtle shimmer, far below the full connecting/processing wave.
      const shapePulse = (morphProgress !== null ? Math.sin(morphProgress * Math.PI) : 0) * SHAPE_WAVE_GAIN;
      const waveAmp = Math.min(1, Math.max(conn * RING_WAVE_ATTACK, shapePulse));
      // The whoosh leaps with the tween pulse but bleeds off exponentially
      // (half-life ~0.6s) so the sprint decelerates into the cruise.
      whooshRef.current = Math.max(Math.max(statePulse, shapePulse), whooshRef.current * Math.exp(-dt * 1.1));
      const whoosh = whooshRef.current * RING_BOOST_PEAK;
      ringPhaseRef.current = (ringPhaseRef.current + (RING_WAVE_SPEED + whoosh) * dt) % 1;
      if (ringPhaseRef.current < 0) ringPhaseRef.current += 1;

      if (ringGL) {
        const cv = ringGLCanvasRef.current;
        if (!cv) { rafId = requestAnimationFrame(animate); return; }
        const { gl, u } = ringGL;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const cw = Math.max(1, Math.round(cv.clientWidth * dpr));
        const ch = Math.max(1, Math.round(cv.clientHeight * dpr));
        if (cv.width !== cw || cv.height !== ch) { cv.width = cw; cv.height = ch; gl.viewport(0, 0, cw, ch); }
        const p = pointerRef.current;
        p.proxS += (p.proxTarget - p.proxS) * 0.1;
        p.rotS += (p.rotTarget * p.proxTarget - p.rotS) * 0.08;
        gl.uniform1f(u.iTime, elapsed);
        gl.uniform3f(u.iResolution, cw, ch, cw / ch);
        // Churn-demo colour: the rim keeps its own fixed colour across her states.
        gl.uniform1f(u.uHue, -12.0);
        gl.uniform1f(u.uRot, p.rotS);
        gl.uniform1f(u.uWave, waveAmp);
        gl.uniform1f(u.uWavePhase, ringPhaseRef.current);
        gl.uniform2f(u.uPointer, p.x, p.y);
        gl.uniform1f(u.uProximity, p.proxS);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      } else {
        // SVG fallback rim: glass-lit gradient swaying around vertical.
        if (ringGradRef.current) {
          const ga = -Math.PI / 2 + Math.sin(elapsed * 0.3) * 0.5;
          const R = 100, gcx = 100, gcy = 100;
          ringGradRef.current.setAttribute('x1', (gcx + Math.cos(ga) * R).toFixed(2));
          ringGradRef.current.setAttribute('y1', (gcy + Math.sin(ga) * R).toFixed(2));
          ringGradRef.current.setAttribute('x2', (gcx - Math.cos(ga) * R).toFixed(2));
          ringGradRef.current.setAttribute('y2', (gcy - Math.sin(ga) * R).toFixed(2));
          const stopAlpha = 0.48 + amp * 0.15 + conn * 0.22;
          const toStop = (c: RGB) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${stopAlpha.toFixed(3)})`;
          ringStop0Ref.current?.setAttribute('stop-color', toStop(palette.ring0));
          ringStop1Ref.current?.setAttribute('stop-color', toStop(palette.ring1));
          ringStop2Ref.current?.setAttribute('stop-color', toStop(palette.ring2));
          ringStop3Ref.current?.setAttribute('stop-color', toStop(palette.ring3));
        }
        const ringD = buildRingPath(ringPhaseRef.current, waveAmp);
        const ringW = RING_STROKE_THIN + (RING_STROKE_THICK - RING_STROKE_THIN) * conn;
        if (ringRef.current) {
          ringRef.current.setAttribute('d', ringD);
          ringRef.current.setAttribute('stroke-width', ringW.toFixed(2));
        }
        if (ringBloomRef.current) {
          ringBloomRef.current.setAttribute('d', ringD);
          ringBloomRef.current.setAttribute('stroke-width', (ringW * RING_BLOOM_WIDTH_MUL).toFixed(2));
          ringBloomRef.current.setAttribute('opacity', (RING_BLOOM_ALPHA * (0.7 + motionAmp * 0.5 + conn * 0.3)).toFixed(3));
        }
      }

      // Inner orb wobble: constant-speed phase, steady drift even when the ring sprints.
      innerPhaseRef.current = (innerPhaseRef.current + INNER_WAVE_SPEED * dt) % 1;
      if (innerPhaseRef.current < 0) innerPhaseRef.current += 1;
      if (innerClipPathRef.current) innerClipPathRef.current.setAttribute('d', buildInnerClipPath(innerPhaseRef.current));

      // ---- 8. Liquid core + the 2D overlay (voice bloom merged, masked dots split). ----
      if (ether) {
        blobTimeRef.current += dt * (1 + conn * 2.5); // dot-field flow clock; connecting stirs it
        const blobTime = blobTimeRef.current;
        // Her states reach into the fluid: voice pushes harder, connecting wanders faster.
        ether.simOptions.mouse_force = 14 * (1 + amp * 1.2 + conn * 0.8);
        ether.setAutoSpeed(0.1 * (1 + conn * 1.5));
        // Living backdrop: drift the liquid's wash around her state base, two
        // incommensurate sines so the tint never loops visibly. BG_DIM keeps it
        // below the full brand lavender so the body does not glare.
        const BG_DIM = 0.76;
        const bgT1 = Math.sin(elapsed * 0.09), bgT2 = Math.sin(elapsed * 0.06 + 2.1);
        ether.setBgColor(
          palette.base[0] * BG_DIM + bgT1 * 3 - bgT2 * 2,
          palette.base[1] * BG_DIM + bgT2 * 3,
          Math.min(255, palette.base[2] * BG_DIM + bgT1 * 2),
        );
        ether.step(t);

        const canvas = canvasRef.current;
        if (canvas) {
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
          const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
          if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const dotted = morph > 0.002;
            ether.canvas.style.opacity = dotted ? '0' : '1';
            ctx.globalCompositeOperation = 'source-over';
            ctx.clearRect(0, 0, w, h);
            if (dotted) ctx.drawImage(ether.canvas, 0, 0, w, h);
            // Voice bloom: she lights from within when speaking.
            if (motionAmp > 0.015) {
              const b = palette.bloom;
              const col = `${b[0] | 0},${b[1] | 0},${b[2] | 0}`;
              const bloomAlpha = Math.min(0.40, motionAmp * 0.45);
              const g = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, Math.max(w, h) * (0.36 + motionAmp * 0.2));
              g.addColorStop(0, `rgba(${col},${bloomAlpha.toFixed(3)})`);
              g.addColorStop(1, `rgba(${col},0)`);
              ctx.globalCompositeOperation = 'lighter';
              ctx.fillStyle = g;
              ctx.fillRect(0, 0, w, h);
              ctx.globalCompositeOperation = 'source-over';
            }
            if (dotted) applyDotMask(ctx, w, h, morph, patternRef.current, elapsed, motionAmp, blobTime, fieldStateRef.current);
          }
        }
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ringGL && rootEl) {
        rootEl.removeEventListener('pointermove', onPointerMove);
        rootEl.removeEventListener('pointerleave', onPointerLeave);
      }
      etherRef.current?.destroy();
      etherRef.current = null;
    };
    // Mount-once: the loop reads live props via refs so it never restarts
    // (keeping `elapsed` continuous across state flips).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={rootRef}
      data-state={state}
      data-shape="orb"
      className={`go-root ${className}`.trim()}
    >
      {/* Zero-size SVG hosting the inner-orb clipPath, referenced by the core. */}
      <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path ref={innerClipPathRef} d={buildInnerClipPath(0)} />
          </clipPath>
        </defs>
      </svg>

      {/* Outer glow: halo blobs + breathing aura behind her (CSS-driven). */}
      <div className="go-halo" aria-hidden="true">
        <div className="go-blob go-blob-a" />
        <div className="go-blob go-blob-b" />
        <div className="go-blob go-blob-c" />
      </div>

      {/* Floating group: constant subtle drift applied to every layer inside. */}
      <div ref={floatRef} className="go-float">
        <div className="go-aura" aria-hidden="true" />
        {/* Legacy blur-ball glow (SVG fallback only). */}
        <div ref={glowRef} className="go-glow" aria-hidden="true" />

        {/* Wavy ring (SVG fallback) painted with a rotating palette gradient. */}
        <svg
          ref={ringSvgRef}
          className="go-ring"
          aria-hidden="true"
          viewBox="0 0 200 200"
          fill="none"
          strokeLinejoin="round"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient ref={ringGradRef} id={ringId} gradientUnits="userSpaceOnUse" x1="0" y1="100" x2="200" y2="100">
              <stop ref={ringStop0Ref} offset="0" stopColor="rgba(168,143,255,0.5)" />
              <stop ref={ringStop1Ref} offset="0.33" stopColor="rgba(168,143,255,0.5)" />
              <stop ref={ringStop2Ref} offset="0.66" stopColor="rgba(110,63,255,0.5)" />
              <stop ref={ringStop3Ref} offset="1" stopColor="rgba(89,41,232,0.5)" />
            </linearGradient>
          </defs>
          <path ref={ringBloomRef} className="go-ring-bloom" stroke={`url(#${ringId})`} strokeWidth={RING_STROKE_THIN * RING_BLOOM_WIDTH_MUL} opacity={RING_BLOOM_ALPHA} d={buildRingPath(0, 0)} />
          <path ref={ringRef} className="go-ring-line" stroke={`url(#${ringId})`} strokeWidth={RING_STROKE_THIN} d={buildRingPath(0, 0)} />
        </svg>

        {/* Core: the liquid core mounts here (in-flow, beneath the overlays);
            the 2D canvas carries the voice bloom and the masked dot field; the
            SVG clip-path gives the outline a subtle wobble. */}
        <div ref={coreRef} className="go-core" style={{ clipPath: `url(#${clipId})` }}>
          <canvas ref={canvasRef} className="go-canvas" aria-hidden="true" />
          <div ref={hlRef} className="go-hl" aria-hidden="true" />
          <div ref={ctaLabelRef} className="go-cta-label" />
        </div>

        {/* WebGL rim (primary): noise band + colour churn + orbiting light. */}
        <canvas ref={ringGLCanvasRef} className="go-ringgl" aria-hidden="true" />
      </div>
    </div>
  );
};

export default GlowingOrb;
